/**
 * Marketing Settings - GDPR-Compliant Preference Center
 * Comprehensive UI for managing merchant marketing preferences
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Settings, Users, Mail, MessageSquare, Bell, Shield,
  CheckCircle, XCircle, Download, Filter, Search,
  Eye, History, AlertTriangle, TrendingUp, Globe,
  ChevronLeft, ChevronRight,
  Tag, Plus, Trash2, Edit3, ChevronUp, ChevronDown, Check, X, Star, Zap
} from 'lucide-react';
import './MarketingSettings.css';

const GENERIC_CRM_FAILURES = new Set(['failure', 'failed', 'request failed with status code 400']);

function getCrmFailureMessage(item = {}) {
  const errorMessage = String(item.errorMessage || '').trim();
  const responseBody = String(item.responseBody || '').trim();
  const isGeneric = GENERIC_CRM_FAILURES.has(errorMessage.toLowerCase());

  if (errorMessage && responseBody && isGeneric) {
    return `${errorMessage}: ${responseBody}`;
  }
  if (errorMessage) return errorMessage;
  if (responseBody) return responseBody;
  return `Zoho rejected this record${item.responseStatus ? ` (${item.responseStatus})` : ''}`;
}

function MarketingSettings({ apiWithAccessControl }) {
  const [activeTab, setActiveTab] = useState('overview');
  const [preferences, setPreferences] = useState([]);
  const [selectedMerchant, setSelectedMerchant] = useState(null);
  const [merchantDetail, setMerchantDetail] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    globalOptOut: false,
    channel: ''
  });
  const [bulkSelection, setBulkSelection] = useState([]);
  const [showBulkUpdate, setShowBulkUpdate] = useState(false);

  // Pagination state
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0
  });

  // Status message state (replaces alert())
  const [statusMessage, setStatusMessage] = useState(null);
  const statusTimerRef = useRef(null);

  // Export loading state
  const [exporting, setExporting] = useState(false);

  // History pagination state
  const [historyData, setHistoryData] = useState([]);
  const [historyPagination, setHistoryPagination] = useState({ limit: 10, offset: 0, total: 0 });
  const [historyLoading, setHistoryLoading] = useState(false);

  // Opt-out confirmation modal state
  const [showOptOutConfirm, setShowOptOutConfirm] = useState(false);
  const [pendingOptOutValue, setPendingOptOutValue] = useState(null);

  // Lead Statuses state
  const [leadStatuses, setLeadStatuses] = useState([]);
  const [lsCategory, setLsCategory] = useState('LEAD');
  const [lsLoading, setLsLoading] = useState(false);
  const [lsEditing, setLsEditing] = useState(null); // null | 'new' | status object
  const [lsForm, setLsForm] = useState({ label: '', value: '', color: '#6366f1', category: 'LEAD', isDefault: false });
  const [lsDeleteConfirm, setLsDeleteConfirm] = useState(null);

  // External CRM sync state
  const [crmIntegration, setCrmIntegration] = useState(null);
  const [crmLogs, setCrmLogs] = useState([]);
  const [crmLoading, setCrmLoading] = useState(false);
  const [crmSaving, setCrmSaving] = useState(false);
  const [crmTesting, setCrmTesting] = useState(false);
  const [crmBulkSyncing, setCrmBulkSyncing] = useState(false);
  const [crmBulkPreviewing, setCrmBulkPreviewing] = useState(false);
  const [crmBulkResult, setCrmBulkResult] = useState(null);
  const [crmBulkForm, setCrmBulkForm] = useState({
    contactType: 'MERCHANT',
    limit: 25,
    offset: 0
  });
  const [crmForm, setCrmForm] = useState({
    provider: 'WEBHOOK',
    name: 'External CRM',
    isEnabled: false,
    webhookUrl: '',
    secret: '',
    clearSecret: false,
    config: {
      zoho: {
        dataCenter: 'US',
        accountsUrl: 'https://accounts.zoho.com',
        apiDomain: 'https://www.zohoapis.com',
        clientId: '',
        clientSecret: '',
        refreshToken: '',
        module: 'Contacts',
        duplicateField: 'Email',
        clearClientSecret: false,
        clearRefreshToken: false
      }
    }
  });

  const zohoDataCenters = {
    US: { label: 'United States', accountsUrl: 'https://accounts.zoho.com', apiDomain: 'https://www.zohoapis.com' },
    EU: { label: 'Europe', accountsUrl: 'https://accounts.zoho.eu', apiDomain: 'https://www.zohoapis.eu' },
    IN: { label: 'India', accountsUrl: 'https://accounts.zoho.in', apiDomain: 'https://www.zohoapis.in' },
    AU: { label: 'Australia', accountsUrl: 'https://accounts.zoho.com.au', apiDomain: 'https://www.zohoapis.com.au' },
    JP: { label: 'Japan', accountsUrl: 'https://accounts.zoho.jp', apiDomain: 'https://www.zohoapis.jp' },
    CA: { label: 'Canada', accountsUrl: 'https://accounts.zohocloud.ca', apiDomain: 'https://www.zohoapis.ca' },
    SA: { label: 'Saudi Arabia', accountsUrl: 'https://accounts.zoho.sa', apiDomain: 'https://www.zohoapis.sa' }
  };

  const updateZohoForm = (updates) => {
    setCrmForm(prev => ({
      ...prev,
      config: {
        ...(prev.config || {}),
        zoho: {
          ...(prev.config?.zoho || {}),
          ...updates
        }
      }
    }));
  };

  /**
   * Show an inline status message banner. Auto-dismisses after 3 seconds.
   */
  const showStatus = useCallback((type, text) => {
    if (statusTimerRef.current) {
      clearTimeout(statusTimerRef.current);
    }
    setStatusMessage({ type, text });
    statusTimerRef.current = setTimeout(() => {
      setStatusMessage(null);
      statusTimerRef.current = null;
    }, 3000);
  }, []);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (statusTimerRef.current) {
        clearTimeout(statusTimerRef.current);
      }
    };
  }, []);

  // Load statistics
  useEffect(() => {
    if (activeTab === 'overview') {
      loadStats();
    }
  }, [activeTab]);

  // Load preferences list
  useEffect(() => {
    if (activeTab === 'merchants') {
      loadPreferences();
    }
  }, [activeTab, filters, pagination.page]);

  const loadStats = async () => {
    setLoading(true);
    try {
      const response = await apiWithAccessControl('/admin/marketing-preferences/stats', {
        method: 'GET'
      });
      const data = await response.json();
      if (data.success) {
        setStats(data.data);
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPreferences = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', pagination.page.toString());
      params.append('limit', pagination.limit.toString());
      if (filters.search) params.append('search', filters.search);
      if (filters.globalOptOut) params.append('globalOptOut', 'true');
      if (filters.channel) params.append('channel', filters.channel);

      const response = await apiWithAccessControl(
        `/admin/marketing-preferences?${params.toString()}`,
        { method: 'GET' }
      );
      const data = await response.json();
      if (data.success) {
        setPreferences(data.data);
        if (data.pagination) {
          setPagination(prev => ({
            ...prev,
            total: data.pagination.total,
            totalPages: data.pagination.totalPages
          }));
        }
      }
    } catch (error) {
      console.error('Error loading preferences:', error);
    } finally {
      setLoading(false);
    }
  };

  // Reset to page 1 when filters change
  useEffect(() => {
    setPagination(prev => ({ ...prev, page: 1 }));
  }, [filters.search, filters.globalOptOut, filters.channel]);

  const loadMerchantDetail = async (merchantId) => {
    setLoading(true);
    try {
      const response = await apiWithAccessControl(
        `/admin/marketing-preferences/${merchantId}`,
        { method: 'GET' }
      );
      const data = await response.json();
      if (data.success) {
        setMerchantDetail(data.data);
        setSelectedMerchant(merchantId);
        setActiveTab('detail');
        loadHistory(merchantId);
      }
    } catch (error) {
      console.error('Error loading merchant detail:', error);
    } finally {
      setLoading(false);
    }
  };

  const updatePreference = async (merchantId, updates) => {
    try {
      const response = await apiWithAccessControl(
        `/admin/marketing-preferences/${merchantId}`,
        {
          method: 'PUT',
          body: JSON.stringify(updates)
        }
      );
      const data = await response.json();
      if (data.success) {
        showStatus('success', 'Preferences updated successfully');
        loadMerchantDetail(merchantId);
      } else {
        showStatus('error', 'Failed to update preferences');
      }
    } catch (error) {
      console.error('Error updating preferences:', error);
      showStatus('error', 'Error updating preferences');
    }
  };

  const bulkUpdate = async (updates) => {
    if (bulkSelection.length === 0) {
      showStatus('error', 'Please select merchants first');
      return;
    }

    try {
      const response = await apiWithAccessControl(
        '/admin/marketing-preferences/bulk-update',
        {
          method: 'POST',
          body: JSON.stringify({
            merchantIds: bulkSelection,
            preferences: updates,
            notes: 'Bulk update by admin'
          })
        }
      );
      const data = await response.json();
      if (data.success) {
        showStatus('success', data.message);
        setBulkSelection([]);
        setShowBulkUpdate(false);
        loadPreferences();
      } else {
        showStatus('error', 'Failed to bulk update');
      }
    } catch (error) {
      console.error('Error bulk updating:', error);
      showStatus('error', 'Error bulk updating preferences');
    }
  };

  const toggleBulkSelection = (merchantId) => {
    setBulkSelection(prev =>
      prev.includes(merchantId)
        ? prev.filter(id => id !== merchantId)
        : [...prev, merchantId]
    );
  };

  /**
   * Export preferences as CSV. Fetches all records with a high limit.
   */
  const exportCSV = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      params.append('page', '1');
      params.append('limit', '10000');
      if (filters.search) params.append('search', filters.search);
      if (filters.globalOptOut) params.append('globalOptOut', 'true');
      if (filters.channel) params.append('channel', filters.channel);

      const response = await apiWithAccessControl(
        `/admin/marketing-preferences?${params.toString()}`,
        { method: 'GET' }
      );
      const data = await response.json();
      if (!data.success || !data.data || data.data.length === 0) {
        showStatus('error', 'No data available to export');
        setExporting(false);
        return;
      }

      const headers = [
        'Email',
        'Username',
        'Tier',
        'Email Marketing',
        'SMS Marketing',
        'Product Updates',
        'Newsletter',
        'Global Opt-out'
      ];

      const rows = data.data.map(pref => [
        pref.merchant?.email || '',
        pref.merchant?.username || '',
        pref.merchant?.merchantTier || '',
        pref.emailMarketing ? 'Yes' : 'No',
        pref.smsMarketing ? 'Yes' : 'No',
        pref.productUpdates ? 'Yes' : 'No',
        pref.newsletterSubscription ? 'Yes' : 'No',
        pref.globalOptOut ? 'Yes' : 'No'
      ]);

      const csvContent = [
        headers.join(','),
        ...rows.map(row =>
          row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')
        )
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `marketing-preferences-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      showStatus('success', `Exported ${data.data.length} records successfully`);
    } catch (error) {
      console.error('Error exporting CSV:', error);
      showStatus('error', 'Failed to export preferences');
    } finally {
      setExporting(false);
    }
  };

  const loadHistory = async (merchantId, offset = 0) => {
    setHistoryLoading(true);
    try {
      const response = await apiWithAccessControl(
        `/admin/marketing-preferences/${merchantId}/history?limit=10&offset=${offset}`,
        { method: 'GET' }
      );
      const data = await response.json();
      if (data.success) {
        setHistoryData(data.data);
        setHistoryPagination({
          limit: parseInt(data.pagination.limit),
          offset: parseInt(data.pagination.offset),
          total: data.pagination.total
        });
      }
    } catch (error) {
      console.error('Error loading history:', error);
    } finally {
      setHistoryLoading(false);
    }
  };

  /**
   * Navigate to a specific page
   */
  const goToPage = (page) => {
    if (page < 1 || page > pagination.totalPages) return;
    setPagination(prev => ({ ...prev, page }));
  };

  /**
   * Generate an array of page numbers to display.
   * Shows at most 5 page buttons around the current page.
   */
  const getPageNumbers = () => {
    const { page, totalPages } = pagination;
    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    let start = Math.max(1, page - 2);
    let end = Math.min(totalPages, start + 4);
    if (end - start < 4) {
      start = Math.max(1, end - 4);
    }
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  // Status Message Banner Component
  const StatusBanner = () => {
    if (!statusMessage) return null;
    return (
      <div className={`mkt-set-status-banner mkt-set-status-${statusMessage.type}`}>
        {statusMessage.type === 'success' ? (
          <CheckCircle size={18} />
        ) : (
          <AlertTriangle size={18} />
        )}
        <span>{statusMessage.text}</span>
        <button
          className="mkt-set-status-dismiss"
          onClick={() => setStatusMessage(null)}
        >
          x
        </button>
      </div>
    );
  };

  // Overview Dashboard
  const OverviewDashboard = () => {
    // Parse numeric rate from the stats string like "72.50%"
    const parseRate = (rateStr) => {
      if (!rateStr) return 0;
      return parseFloat(rateStr.replace('%', '')) || 0;
    };

    return (
      <div className="mkt-set-overview">
        <div className="mkt-set-header">
          <div>
            <h1>Marketing Preferences Management</h1>
            <p>GDPR-compliant merchant communication preferences</p>
          </div>
          <button className="mkt-set-btn-primary" onClick={() => setActiveTab('merchants')}>
            <Users size={18} />
            Manage Preferences
          </button>
        </div>

        {loading && <div className="mkt-set-loading">Loading statistics...</div>}

        {stats && (
          <>
            {/* Quick Stats */}
            <div className="mkt-set-stats-grid">
              <div className="mkt-set-stat-card">
                <div className="mkt-set-stat-icon" style={{ background: '#e8f5f3', color: '#155a51' }}>
                  <Users size={32} />
                </div>
                <div className="mkt-set-stat-content">
                  <h3>{stats.totalMerchants}</h3>
                  <p>Total Merchants</p>
                </div>
              </div>

              <div className="mkt-set-stat-card">
                <div className="mkt-set-stat-icon" style={{ background: '#e3f2fd', color: '#1976d2' }}>
                  <CheckCircle size={32} />
                </div>
                <div className="mkt-set-stat-content">
                  <h3>{stats.optInStats.email.count}</h3>
                  <p>Email Opt-ins ({stats.optInStats.email.rate})</p>
                </div>
              </div>

              <div className="mkt-set-stat-card">
                <div className="mkt-set-stat-icon" style={{ background: '#f3e5f5', color: '#7b1fa2' }}>
                  <MessageSquare size={32} />
                </div>
                <div className="mkt-set-stat-content">
                  <h3>{stats.optInStats.sms.count}</h3>
                  <p>SMS Opt-ins ({stats.optInStats.sms.rate})</p>
                </div>
              </div>

              <div className="mkt-set-stat-card">
                <div className="mkt-set-stat-icon" style={{ background: '#fff3e0', color: '#f57c00' }}>
                  <Globe size={32} />
                </div>
                <div className="mkt-set-stat-content">
                  <h3>{stats.globalOptOuts}</h3>
                  <p>Global Opt-outs</p>
                </div>
              </div>
            </div>

            {/* Channel Opt-in Rate Breakdown */}
            <div className="mkt-set-channel-breakdown">
              <h2>Channel Opt-in Rates</h2>
              <div className="mkt-set-channel-list">
                {/* Email Marketing */}
                <div className="mkt-set-channel-row">
                  <div className="mkt-set-channel-label">
                    <Mail size={16} />
                    <span>Email Marketing</span>
                  </div>
                  <div className="mkt-set-channel-bar-wrapper">
                    <div className="mkt-set-channel-bar">
                      <div
                        className="mkt-set-channel-bar-fill"
                        style={{
                          width: `${parseRate(stats.optInStats.email.rate)}%`,
                          background: '#1976d2'
                        }}
                      />
                    </div>
                    <span className="mkt-set-channel-rate">{stats.optInStats.email.rate}</span>
                  </div>
                </div>

                {/* SMS Marketing */}
                <div className="mkt-set-channel-row">
                  <div className="mkt-set-channel-label">
                    <MessageSquare size={16} />
                    <span>SMS Marketing</span>
                  </div>
                  <div className="mkt-set-channel-bar-wrapper">
                    <div className="mkt-set-channel-bar">
                      <div
                        className="mkt-set-channel-bar-fill"
                        style={{
                          width: `${parseRate(stats.optInStats.sms.rate)}%`,
                          background: '#7b1fa2'
                        }}
                      />
                    </div>
                    <span className="mkt-set-channel-rate">{stats.optInStats.sms.rate}</span>
                  </div>
                </div>

                {/* Product Updates */}
                <div className="mkt-set-channel-row">
                  <div className="mkt-set-channel-label">
                    <Bell size={16} />
                    <span>Product Updates</span>
                  </div>
                  <div className="mkt-set-channel-bar-wrapper">
                    <div className="mkt-set-channel-bar">
                      <div
                        className="mkt-set-channel-bar-fill"
                        style={{
                          width: `${parseRate(stats.optInStats.productUpdates?.rate)}%`,
                          background: '#2e7d32'
                        }}
                      />
                    </div>
                    <span className="mkt-set-channel-rate">{stats.optInStats.productUpdates?.rate || 'N/A'}</span>
                  </div>
                </div>

                {/* Newsletter */}
                <div className="mkt-set-channel-row">
                  <div className="mkt-set-channel-label">
                    <Mail size={16} />
                    <span>Newsletter</span>
                  </div>
                  <div className="mkt-set-channel-bar-wrapper">
                    <div className="mkt-set-channel-bar">
                      <div
                        className="mkt-set-channel-bar-fill"
                        style={{
                          width: `${parseRate(stats.optInStats.newsletter?.rate)}%`,
                          background: '#0288d1'
                        }}
                      />
                    </div>
                    <span className="mkt-set-channel-rate">{stats.optInStats.newsletter?.rate || 'N/A'}</span>
                  </div>
                </div>

                {/* Platform Announcements */}
                <div className="mkt-set-channel-row">
                  <div className="mkt-set-channel-label">
                    <Globe size={16} />
                    <span>Platform Announcements</span>
                  </div>
                  <div className="mkt-set-channel-bar-wrapper">
                    <div className="mkt-set-channel-bar">
                      <div
                        className="mkt-set-channel-bar-fill"
                        style={{
                          width: `${parseRate(stats.optInStats.platformAnnouncements?.rate)}%`,
                          background: '#e65100'
                        }}
                      />
                    </div>
                    <span className="mkt-set-channel-rate">{stats.optInStats.platformAnnouncements?.rate || 'N/A'}</span>
                  </div>
                </div>

                {/* Promotional Campaigns */}
                <div className="mkt-set-channel-row">
                  <div className="mkt-set-channel-label">
                    <TrendingUp size={16} />
                    <span>Promotional Campaigns</span>
                  </div>
                  <div className="mkt-set-channel-bar-wrapper">
                    <div className="mkt-set-channel-bar">
                      <div
                        className="mkt-set-channel-bar-fill"
                        style={{
                          width: `${parseRate(stats.optInStats.promotionalCampaigns?.rate)}%`,
                          background: '#ad1457'
                        }}
                      />
                    </div>
                    <span className="mkt-set-channel-rate">{stats.optInStats.promotionalCampaigns?.rate || 'N/A'}</span>
                  </div>
                </div>

                {/* Educational Content */}
                <div className="mkt-set-channel-row">
                  <div className="mkt-set-channel-label">
                    <TrendingUp size={16} />
                    <span>Educational Content</span>
                  </div>
                  <div className="mkt-set-channel-bar-wrapper">
                    <div className="mkt-set-channel-bar">
                      <div
                        className="mkt-set-channel-bar-fill"
                        style={{
                          width: `${parseRate(stats.optInStats.educationalContent?.rate)}%`,
                          background: '#4527a0'
                        }}
                      />
                    </div>
                    <span className="mkt-set-channel-rate">{stats.optInStats.educationalContent?.rate || 'N/A'}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Info Cards */}
            <div className="mkt-set-info-section">
              <div className="mkt-set-info-card">
                <Shield size={24} className="mkt-set-info-icon" />
                <div>
                  <h3>GDPR Compliance</h3>
                  <p>All preference changes are logged with timestamps, IP addresses, and consent sources for full compliance with GDPR regulations.</p>
                </div>
              </div>

              <div className="mkt-set-info-card">
                <TrendingUp size={24} className="mkt-set-info-icon" />
                <div>
                  <h3>Recent Activity</h3>
                  <p>{stats.recentChanges} preference changes in the last 7 days</p>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    );
  };

  // Merchants List View
  const MerchantsListView = () => {
    const startItem = pagination.total === 0 ? 0 : (pagination.page - 1) * pagination.limit + 1;
    const endItem = Math.min(pagination.page * pagination.limit, pagination.total);

    return (
      <div className="mkt-set-merchants-view">
        <div className="mkt-set-view-header">
          <div>
            <h1>
              Merchant Preferences
              {pagination.total > 0 && (
                <span className="mkt-set-merchant-count">({pagination.total} merchants)</span>
              )}
            </h1>
            <p>Manage individual merchant communication preferences</p>
          </div>
          <div className="mkt-set-view-header-actions">
            <button
              className="mkt-set-btn-secondary"
              onClick={exportCSV}
              disabled={exporting}
            >
              <Download size={18} />
              {exporting ? 'Exporting...' : 'Export CSV'}
            </button>
            {bulkSelection.length > 0 && (
              <button
                className="mkt-set-btn-primary"
                onClick={() => setShowBulkUpdate(true)}
              >
                <Settings size={18} />
                Bulk Update ({bulkSelection.length})
              </button>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="mkt-set-filters">
          <div className="mkt-set-search">
            <Search size={18} />
            <input
              type="text"
              placeholder="Search by email or username..."
              value={filters.search}
              onChange={(e) => setFilters({...filters, search: e.target.value})}
            />
          </div>

          <select
            value={filters.channel}
            onChange={(e) => setFilters({...filters, channel: e.target.value})}
          >
            <option value="">All Channels</option>
            <option value="emailMarketing">Email Marketing</option>
            <option value="smsMarketing">SMS Marketing</option>
            <option value="productUpdates">Product Updates</option>
            <option value="newsletterSubscription">Newsletter</option>
          </select>

          <label className="mkt-set-filter-checkbox">
            <input
              type="checkbox"
              checked={filters.globalOptOut}
              onChange={(e) => setFilters({...filters, globalOptOut: e.target.checked})}
            />
            <span>Only Global Opt-outs</span>
          </label>
        </div>

        {/* Merchants Table */}
        {loading ? (
          <div className="mkt-set-loading">Loading merchants...</div>
        ) : (
          <>
            <div className="mkt-set-table">
              <table>
                <thead>
                  <tr>
                    <th>
                      <input
                        type="checkbox"
                        checked={bulkSelection.length === preferences.length && preferences.length > 0}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setBulkSelection(preferences.map(p => p.merchantId));
                          } else {
                            setBulkSelection([]);
                          }
                        }}
                      />
                    </th>
                    <th>Merchant</th>
                    <th>Email</th>
                    <th>SMS</th>
                    <th>Product Updates</th>
                    <th>Newsletter</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {preferences.map((pref) => (
                    <tr key={pref.id}>
                      <td>
                        <input
                          type="checkbox"
                          checked={bulkSelection.includes(pref.merchantId)}
                          onChange={() => toggleBulkSelection(pref.merchantId)}
                        />
                      </td>
                      <td>
                        <div className="mkt-set-merchant-info">
                          <strong>{pref.merchant.username || pref.merchant.email}</strong>
                          <small>{pref.merchant.merchantTier}</small>
                        </div>
                      </td>
                      <td>
                        {pref.emailMarketing ? (
                          <CheckCircle size={18} className="mkt-set-status-icon success" />
                        ) : (
                          <XCircle size={18} className="mkt-set-status-icon error" />
                        )}
                      </td>
                      <td>
                        {pref.smsMarketing ? (
                          <CheckCircle size={18} className="mkt-set-status-icon success" />
                        ) : (
                          <XCircle size={18} className="mkt-set-status-icon error" />
                        )}
                      </td>
                      <td>
                        {pref.productUpdates ? (
                          <CheckCircle size={18} className="mkt-set-status-icon success" />
                        ) : (
                          <XCircle size={18} className="mkt-set-status-icon error" />
                        )}
                      </td>
                      <td>
                        {pref.newsletterSubscription ? (
                          <CheckCircle size={18} className="mkt-set-status-icon success" />
                        ) : (
                          <XCircle size={18} className="mkt-set-status-icon error" />
                        )}
                      </td>
                      <td>
                        {pref.globalOptOut ? (
                          <span className="mkt-set-status-badge error">Opted Out</span>
                        ) : (
                          <span className="mkt-set-status-badge success">Active</span>
                        )}
                      </td>
                      <td>
                        <button
                          className="mkt-set-btn-icon"
                          onClick={() => loadMerchantDetail(pref.merchantId)}
                          title="View Details"
                        >
                          <Eye size={18} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {preferences.length === 0 && (
                <div className="mkt-set-empty">
                  <Users size={48} />
                  <p>No merchants found</p>
                </div>
              )}
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="mkt-set-pagination">
                <span className="mkt-set-pagination-info">
                  Showing {startItem}-{endItem} of {pagination.total} merchants
                </span>
                <div className="mkt-set-pagination-controls">
                  <button
                    className="mkt-set-pagination-btn"
                    onClick={() => goToPage(pagination.page - 1)}
                    disabled={pagination.page <= 1}
                  >
                    <ChevronLeft size={16} />
                    Previous
                  </button>
                  {getPageNumbers().map(num => (
                    <button
                      key={num}
                      className={`mkt-set-pagination-num ${num === pagination.page ? 'active' : ''}`}
                      onClick={() => goToPage(num)}
                    >
                      {num}
                    </button>
                  ))}
                  <button
                    className="mkt-set-pagination-btn"
                    onClick={() => goToPage(pagination.page + 1)}
                    disabled={pagination.page >= pagination.totalPages}
                  >
                    Next
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            )}

            {/* Show count even with single page */}
            {pagination.totalPages <= 1 && pagination.total > 0 && (
              <div className="mkt-set-pagination">
                <span className="mkt-set-pagination-info">
                  Showing {startItem}-{endItem} of {pagination.total} merchants
                </span>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  // Merchant Detail View
  const MerchantDetailView = () => {
    if (!merchantDetail) return null;

    return (
      <div className="mkt-set-detail">
        <div className="mkt-set-detail-header">
          <button className="mkt-set-btn-back" onClick={() => setActiveTab('merchants')}>
            ← Back to List
          </button>
          <div className="mkt-set-merchant-title">
            <h1>{merchantDetail.merchant.username || merchantDetail.merchant.email}</h1>
            <span className="mkt-set-merchant-tier">{merchantDetail.merchant.merchantTier}</span>
          </div>
        </div>

        {/* Preference Toggles */}
        <div className="mkt-set-prefs-section">
          <h2>Communication Preferences</h2>
          <div className="mkt-set-prefs-grid">
            <PreferenceToggle
              icon={<Mail />}
              label="Email Marketing"
              description="Promotional campaigns and special offers"
              checked={merchantDetail.emailMarketing}
              onChange={(checked) => updatePreference(merchantDetail.merchantId, { emailMarketing: checked })}
            />
            <PreferenceToggle
              icon={<MessageSquare />}
              label="SMS Marketing"
              description="Text message promotions"
              checked={merchantDetail.smsMarketing}
              onChange={(checked) => updatePreference(merchantDetail.merchantId, { smsMarketing: checked })}
            />
            <PreferenceToggle
              icon={<Bell />}
              label="Product Updates"
              description="New features and product announcements"
              checked={merchantDetail.productUpdates}
              onChange={(checked) => updatePreference(merchantDetail.merchantId, { productUpdates: checked })}
            />
            <PreferenceToggle
              icon={<Bell />}
              label="Platform Announcements"
              description="Important platform updates"
              checked={merchantDetail.platformAnnouncements}
              onChange={(checked) => updatePreference(merchantDetail.merchantId, { platformAnnouncements: checked })}
            />
            <PreferenceToggle
              icon={<Mail />}
              label="Newsletter"
              description="Weekly newsletter subscription"
              checked={merchantDetail.newsletterSubscription}
              onChange={(checked) => updatePreference(merchantDetail.merchantId, { newsletterSubscription: checked })}
            />
            <PreferenceToggle
              icon={<TrendingUp />}
              label="Educational Content"
              description="Tips, guides, and best practices"
              checked={merchantDetail.educationalContent}
              onChange={(checked) => updatePreference(merchantDetail.merchantId, { educationalContent: checked })}
            />
          </div>

          {/* Global Opt-out */}
          <div className="mkt-set-global-optout">
            <AlertTriangle size={20} />
            <div>
              <h3>Global Opt-out</h3>
              <p>Unsubscribe from all marketing communications</p>
            </div>
            <label className="mkt-set-toggle">
              <input
                type="checkbox"
                checked={merchantDetail.globalOptOut}
                onChange={(e) => {
                  setPendingOptOutValue(e.target.checked);
                  setShowOptOutConfirm(true);
                }}
              />
              <span className="mkt-set-toggle-slider"></span>
            </label>
          </div>
        </div>

        {/* GDPR Compliance Info */}
        <div className="mkt-set-compliance">
          <h2>GDPR Compliance Information</h2>
          <div className="mkt-set-compliance-grid">
            <div className="mkt-set-compliance-item">
              <strong>Consent Source:</strong>
              <span>{merchantDetail.consentSource || 'N/A'}</span>
            </div>
            <div className="mkt-set-compliance-item">
              <strong>Consent IP Address:</strong>
              <span>{merchantDetail.consentIpAddress || 'N/A'}</span>
            </div>
            <div className="mkt-set-compliance-item">
              <strong>Consent Timestamp:</strong>
              <span>{merchantDetail.consentTimestamp ? new Date(merchantDetail.consentTimestamp).toLocaleString() : 'N/A'}</span>
            </div>
            <div className="mkt-set-compliance-item">
              <strong>Last Updated By:</strong>
              <span>{merchantDetail.lastUpdatedBy || 'N/A'}</span>
            </div>
          </div>
        </div>

        {/* Change History */}
        <div className="mkt-set-history">
          <h2>
            <History size={20} />
            Change History
            {historyPagination.total > 0 && (
              <span className="mkt-set-history-count">({historyPagination.total} entries)</span>
            )}
          </h2>
          {historyLoading ? (
            <div className="mkt-set-loading">Loading history...</div>
          ) : historyData.length > 0 ? (
            <>
              <div className="mkt-set-history-list">
                {historyData.map((entry) => (
                  <div key={entry.id} className="mkt-set-history-item">
                    <div className="mkt-set-history-icon">
                      {entry.action === 'OPT_IN' || entry.action === 'INITIAL_CONSENT' || entry.action === 'PREFERENCE_RESTORED' ? (
                        <CheckCircle size={16} className="success" />
                      ) : entry.action === 'GLOBAL_OPT_OUT' ? (
                        <AlertTriangle size={16} className="warning" />
                      ) : (
                        <XCircle size={16} className="error" />
                      )}
                    </div>
                    <div className="mkt-set-history-content">
                      <strong>{entry.action.replace(/_/g, ' ')}</strong>
                      {entry.channel && <span> - {entry.channel}</span>}
                      <div className="mkt-set-history-meta">
                        {new Date(entry.createdAt).toLocaleString()} •
                        Changed by: {entry.changedBy} •
                        Source: {entry.source}
                        {entry.notes && ` • ${entry.notes}`}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {/* History Pagination */}
              {historyPagination.total > historyPagination.limit && (
                <div className="mkt-set-history-pagination">
                  <span className="mkt-set-pagination-info">
                    Showing {historyPagination.offset + 1}-{Math.min(historyPagination.offset + historyPagination.limit, historyPagination.total)} of {historyPagination.total}
                  </span>
                  <div className="mkt-set-pagination-controls">
                    <button
                      className="mkt-set-pagination-btn"
                      onClick={() => loadHistory(merchantDetail.merchantId, Math.max(0, historyPagination.offset - historyPagination.limit))}
                      disabled={historyPagination.offset <= 0}
                    >
                      <ChevronLeft size={16} />
                      Newer
                    </button>
                    <button
                      className="mkt-set-pagination-btn"
                      onClick={() => loadHistory(merchantDetail.merchantId, historyPagination.offset + historyPagination.limit)}
                      disabled={historyPagination.offset + historyPagination.limit >= historyPagination.total}
                    >
                      Older
                      <ChevronRight size={16} />
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <p className="mkt-set-history-empty">No change history available</p>
          )}
        </div>
      </div>
    );
  };

  // Preference Toggle Component
  const PreferenceToggle = ({ icon, label, description, checked, onChange }) => (
    <div className="mkt-set-pref-toggle">
      <div className="mkt-set-pref-icon">{icon}</div>
      <div className="mkt-set-pref-content">
        <h3>{label}</h3>
        <p>{description}</p>
      </div>
      <label className="mkt-set-toggle">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
        />
        <span className="mkt-set-toggle-slider"></span>
      </label>
    </div>
  );

  // --- Lead Statuses Logic ---
  const loadLeadStatuses = useCallback(async (category) => {
    setLsLoading(true);
    try {
      const response = await apiWithAccessControl(
        `/admin/lead-statuses?category=${category}`,
        { method: 'GET' }
      );
      const data = await response.json();
      if (data.success) {
        const sorted = (data.statuses || data.data || []).sort((a, b) => a.order - b.order);
        setLeadStatuses(sorted);
      }
    } catch (error) {
      console.error('Error loading lead statuses:', error);
      showStatus('error', 'Failed to load lead statuses');
    } finally {
      setLsLoading(false);
    }
  }, [apiWithAccessControl, showStatus]);

  useEffect(() => {
    if (activeTab === 'lead-statuses') {
      loadLeadStatuses(lsCategory);
    }
  }, [activeTab, lsCategory]);

  const loadCrmSettings = useCallback(async () => {
    setCrmLoading(true);
    try {
      const [settingsRes, logsRes] = await Promise.all([
        apiWithAccessControl('/admin/crm/integration', { method: 'GET' }),
        apiWithAccessControl('/admin/crm/logs?limit=8', { method: 'GET' })
      ]);
      const settingsData = await settingsRes.json();
      const logsData = await logsRes.json();

      if (settingsData.success) {
        const integration = settingsData.integration;
        const zoho = integration?.config?.zoho || {};
        setCrmIntegration(integration);
        setCrmForm({
          provider: integration?.provider || 'WEBHOOK',
          name: integration?.name || 'External CRM',
          isEnabled: Boolean(integration?.isEnabled),
          webhookUrl: integration?.webhookUrl || '',
          secret: '',
          clearSecret: false,
          config: {
            ...(integration?.config || {}),
            zoho: {
              dataCenter: zoho.dataCenter || 'US',
              accountsUrl: zoho.accountsUrl || 'https://accounts.zoho.com',
              apiDomain: zoho.apiDomain || 'https://www.zohoapis.com',
              clientId: zoho.clientId || '',
              clientSecret: '',
              refreshToken: '',
              module: zoho.module || 'Contacts',
              duplicateField: zoho.duplicateField || 'Email',
              clearClientSecret: false,
              clearRefreshToken: false
            }
          }
        });
      }

      if (logsData.success) {
        setCrmLogs(logsData.logs || []);
      }
    } catch (error) {
      console.error('Error loading CRM settings:', error);
      showStatus('error', 'Failed to load CRM settings');
    } finally {
      setCrmLoading(false);
    }
  }, [apiWithAccessControl, showStatus]);

  useEffect(() => {
    if (activeTab === 'crm-sync') {
      loadCrmSettings();
    }
  }, [activeTab, loadCrmSettings]);

  const saveCrmSettings = async () => {
    setCrmSaving(true);
    try {
      const response = await apiWithAccessControl('/admin/crm/integration', {
        method: 'PUT',
        body: JSON.stringify(crmForm)
      });
      const data = await response.json();
      if (data.success) {
        showStatus('success', 'CRM integration saved');
        setCrmIntegration(data.integration);
        setCrmForm(prev => ({
          ...prev,
          secret: '',
          clearSecret: false,
          config: {
            ...(prev.config || {}),
            zoho: {
              ...(prev.config?.zoho || {}),
              clientSecret: '',
              refreshToken: '',
              clearClientSecret: false,
              clearRefreshToken: false
            }
          }
        }));
        loadCrmSettings();
      } else {
        showStatus('error', data.message || 'Failed to save CRM integration');
      }
    } catch (error) {
      console.error('Error saving CRM settings:', error);
      showStatus('error', 'Error saving CRM integration');
    } finally {
      setCrmSaving(false);
    }
  };

  const sendCrmTest = async () => {
    setCrmTesting(true);
    try {
      const response = await apiWithAccessControl('/admin/crm/integration/test', {
        method: 'POST'
      });
      const data = await response.json();
      if (data.success) {
        showStatus('success', data.message || 'CRM test delivered');
      } else {
        showStatus('error', data.message || 'CRM test failed');
      }
      loadCrmSettings();
    } catch (error) {
      console.error('Error testing CRM integration:', error);
      showStatus('error', 'Error testing CRM integration');
    } finally {
      setCrmTesting(false);
    }
  };

  const runCrmBulkSync = async ({ dryRun = false } = {}) => {
    if (dryRun) {
      setCrmBulkPreviewing(true);
    } else {
      setCrmBulkSyncing(true);
    }

    try {
      const response = await apiWithAccessControl('/admin/crm/sync-bulk', {
        method: 'POST',
        body: JSON.stringify({
          ...crmBulkForm,
          limit: Number(crmBulkForm.limit) || 25,
          offset: Number(crmBulkForm.offset) || 0,
          dryRun
        })
      });
      const data = await response.json();
      if (data.success) {
        setCrmBulkResult(data.result);
        showStatus('success', data.message || (dryRun ? 'CRM preview ready' : 'CRM bulk sync completed'));
        if (!dryRun) {
          loadCrmSettings();
        }
      } else {
        setCrmBulkResult(data.result || null);
        showStatus('error', data.message || 'CRM bulk sync failed');
      }
    } catch (error) {
      console.error('Error running CRM bulk sync:', error);
      showStatus('error', 'Error running CRM bulk sync');
    } finally {
      setCrmBulkPreviewing(false);
      setCrmBulkSyncing(false);
    }
  };

  const lsStartCreate = () => {
    setLsEditing('new');
    setLsForm({ label: '', value: '', color: '#6366f1', category: lsCategory, isDefault: false });
  };

  const lsStartEdit = (status) => {
    setLsEditing(status);
    setLsForm({ label: status.label, value: status.value, color: status.color, category: status.category, isDefault: status.isDefault });
  };

  const lsCancelForm = () => {
    setLsEditing(null);
    setLsForm({ label: '', value: '', color: '#6366f1', category: lsCategory, isDefault: false });
  };

  const lsSaveStatus = async () => {
    if (!lsForm.label.trim() || !lsForm.value.trim()) {
      showStatus('error', 'Label and value are required');
      return;
    }
    try {
      if (lsEditing === 'new') {
        const response = await apiWithAccessControl('/admin/lead-statuses', {
          method: 'POST',
          body: JSON.stringify({
            label: lsForm.label.trim(),
            value: lsForm.value.trim().toUpperCase(),
            color: lsForm.color,
            order: leadStatuses.length,
            category: lsForm.category,
            isDefault: lsForm.isDefault
          })
        });
        const data = await response.json();
        if (data.success) {
          showStatus('success', 'Status created successfully');
        } else {
          showStatus('error', data.message || 'Failed to create status');
          return;
        }
      } else {
        const response = await apiWithAccessControl(`/admin/lead-statuses/${lsEditing.id}`, {
          method: 'PUT',
          body: JSON.stringify({
            label: lsForm.label.trim(),
            color: lsForm.color,
            isDefault: lsForm.isDefault
          })
        });
        const data = await response.json();
        if (data.success) {
          showStatus('success', 'Status updated successfully');
        } else {
          showStatus('error', data.message || 'Failed to update status');
          return;
        }
      }
      lsCancelForm();
      loadLeadStatuses(lsCategory);
    } catch (error) {
      console.error('Error saving lead status:', error);
      showStatus('error', 'Error saving status');
    }
  };

  const lsToggleActive = async (status) => {
    try {
      const response = await apiWithAccessControl(`/admin/lead-statuses/${status.id}`, {
        method: 'PUT',
        body: JSON.stringify({ isActive: !status.isActive })
      });
      const data = await response.json();
      if (data.success) {
        showStatus('success', `Status ${status.isActive ? 'deactivated' : 'activated'}`);
        loadLeadStatuses(lsCategory);
      } else {
        showStatus('error', data.message || 'Failed to toggle status');
      }
    } catch (error) {
      showStatus('error', 'Error toggling status');
    }
  };

  const lsDeleteStatus = async (id) => {
    try {
      const response = await apiWithAccessControl(`/admin/lead-statuses/${id}`, {
        method: 'DELETE'
      });
      const data = await response.json();
      if (data.success) {
        showStatus('success', 'Status deleted successfully');
        setLsDeleteConfirm(null);
        loadLeadStatuses(lsCategory);
      } else {
        showStatus('error', data.message || 'Cannot delete status — it may be in use');
        setLsDeleteConfirm(null);
      }
    } catch (error) {
      showStatus('error', 'Error deleting status');
      setLsDeleteConfirm(null);
    }
  };

  const lsReorder = async (index, direction) => {
    const newList = [...leadStatuses];
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= newList.length) return;
    [newList[index], newList[swapIndex]] = [newList[swapIndex], newList[index]];
    const orders = newList.map((s, i) => ({ id: s.id, order: i }));
    setLeadStatuses(newList);
    try {
      const response = await apiWithAccessControl('/admin/lead-statuses/reorder', {
        method: 'PUT',
        body: JSON.stringify({ orders })
      });
      const data = await response.json();
      if (!data.success) {
        showStatus('error', 'Failed to reorder');
        loadLeadStatuses(lsCategory);
      }
    } catch (error) {
      showStatus('error', 'Error reordering statuses');
      loadLeadStatuses(lsCategory);
    }
  };

  const LS_PRESET_COLORS = [
    '#6366f1', '#8b5cf6', '#3b82f6', '#0ea5e9', '#06b6d4',
    '#14b8a6', '#10b981', '#f59e0b', '#ef4444', '#6b7280'
  ];

  // Lead Statuses Tab - inline JSX (not a sub-component, to preserve input focus)
  const leadStatusesTabContent = (
    <div className="mkt-set-ls-container">
      <div className="mkt-set-ls-header">
        <div>
          <h1>Lead Status Configuration</h1>
          <p>Manage configurable statuses for leads and waitlist entries</p>
        </div>
      </div>

      {/* Category Toggle */}
      <div className="mkt-set-ls-category-toggle">
        <button
          className={`mkt-set-ls-cat-btn ${lsCategory === 'LEAD' ? 'active' : ''}`}
          onClick={() => setLsCategory('LEAD')}
        >
          Lead Statuses
        </button>
        <button
          className={`mkt-set-ls-cat-btn ${lsCategory === 'WAITLIST' ? 'active' : ''}`}
          onClick={() => setLsCategory('WAITLIST')}
        >
          Waitlist Statuses
        </button>
      </div>

      {lsLoading ? (
        <div className="mkt-set-loading">Loading statuses...</div>
      ) : (
        <div className="mkt-set-ls-list-card">
          {/* Status List */}
          {leadStatuses.length === 0 && !lsEditing && (
            <div className="mkt-set-ls-empty">
              <Tag size={40} />
              <p>No {lsCategory.toLowerCase()} statuses configured yet</p>
            </div>
          )}

          {leadStatuses.map((status, index) => (
            <div key={status.id} className={`mkt-set-ls-row ${!status.isActive ? 'mkt-set-ls-row-inactive' : ''}`}>
              <div className="mkt-set-ls-row-left">
                {/* Reorder Buttons */}
                <div className="mkt-set-ls-reorder">
                  <button
                    className="mkt-set-btn-icon"
                    onClick={() => lsReorder(index, 'up')}
                    disabled={index === 0}
                    title="Move up"
                  >
                    <ChevronUp size={16} />
                  </button>
                  <button
                    className="mkt-set-btn-icon"
                    onClick={() => lsReorder(index, 'down')}
                    disabled={index === leadStatuses.length - 1}
                    title="Move down"
                  >
                    <ChevronDown size={16} />
                  </button>
                </div>

                {/* Color Swatch */}
                <span
                  className="mkt-set-ls-swatch"
                  style={{ background: status.color }}
                />

                {/* Label + Value */}
                <div className="mkt-set-ls-info">
                  <span className="mkt-set-ls-label">{status.label}</span>
                  <span className="mkt-set-ls-value">{status.value}</span>
                </div>

                {/* Default Badge */}
                {status.isDefault && (
                  <span className="mkt-set-ls-default-badge">
                    <Star size={12} />
                    Default
                  </span>
                )}
              </div>

              <div className="mkt-set-ls-row-right">
                {/* Active Toggle */}
                <label className="mkt-set-toggle" title={status.isActive ? 'Active' : 'Inactive'}>
                  <input
                    type="checkbox"
                    checked={status.isActive}
                    onChange={() => lsToggleActive(status)}
                  />
                  <span className="mkt-set-toggle-slider"></span>
                </label>

                {/* Edit Button */}
                <button
                  className="mkt-set-btn-icon"
                  onClick={() => lsStartEdit(status)}
                  title="Edit"
                >
                  <Edit3 size={16} />
                </button>

                {/* Delete Button */}
                <button
                  className="mkt-set-btn-icon mkt-set-ls-delete-btn"
                  onClick={() => setLsDeleteConfirm(status.id)}
                  title="Delete"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}

          {/* Inline Form for Add/Edit */}
          {lsEditing && (
            <div className="mkt-set-ls-form">
              <h3>{lsEditing === 'new' ? 'Add New Status' : 'Edit Status'}</h3>
              <div className="mkt-set-ls-form-fields">
                <div className="mkt-set-ls-field">
                  <label>Label</label>
                  <input
                    type="text"
                    value={lsForm.label}
                    onChange={(e) => {
                      const label = e.target.value;
                      const autoValue = label.trim().toUpperCase().replace(/\s+/g, '_').replace(/[^A-Z0-9_]/g, '');
                      setLsForm(prev => ({
                        ...prev,
                        label,
                        ...(lsEditing === 'new' ? { value: autoValue } : {})
                      }));
                    }}
                    placeholder="e.g. New Lead"
                  />
                  {lsEditing === 'new' && lsForm.value && (
                    <span style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>Code: {lsForm.value}</span>
                  )}
                </div>
                {lsEditing === 'new' && (
                  <div className="mkt-set-ls-field">
                    <label>Category</label>
                    <select
                      value={lsForm.category}
                      onChange={(e) => setLsForm({ ...lsForm, category: e.target.value })}
                    >
                      <option value="LEAD">Lead</option>
                      <option value="WAITLIST">Waitlist</option>
                    </select>
                  </div>
                )}
                <div className="mkt-set-ls-field">
                  <label>Color</label>
                  <div className="mkt-set-ls-color-swatches">
                    {LS_PRESET_COLORS.map((c) => (
                      <button
                        key={c}
                        className={`mkt-set-ls-color-btn ${lsForm.color === c ? 'active' : ''}`}
                        style={{ background: c }}
                        onClick={() => setLsForm({ ...lsForm, color: c })}
                        type="button"
                        title={c}
                      >
                        {lsForm.color === c && <Check size={14} color="#fff" />}
                      </button>
                    ))}
                  </div>
                </div>
                <label className="mkt-set-ls-checkbox-row">
                  <input
                    type="checkbox"
                    checked={lsForm.isDefault}
                    onChange={(e) => setLsForm({ ...lsForm, isDefault: e.target.checked })}
                  />
                  <span>Set as default status</span>
                </label>
              </div>
              <div className="mkt-set-ls-form-actions">
                <button className="mkt-set-btn-secondary" onClick={lsCancelForm}>
                  <X size={16} />
                  Cancel
                </button>
                <button className="mkt-set-btn-primary" onClick={lsSaveStatus}>
                  <Check size={16} />
                  {lsEditing === 'new' ? 'Create Status' : 'Save Changes'}
                </button>
              </div>
            </div>
          )}

          {/* Add Button */}
          {!lsEditing && (
            <button className="mkt-set-ls-add-btn" onClick={lsStartCreate}>
              <Plus size={18} />
              Add Status
            </button>
          )}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {lsDeleteConfirm && (
        <div className="mkt-set-modal-overlay" onClick={() => setLsDeleteConfirm(null)}>
          <div className="mkt-set-modal" onClick={(e) => e.stopPropagation()}>
            <div className="mkt-set-modal-header">
              <h2>Delete Status</h2>
              <button className="mkt-set-btn-icon" onClick={() => setLsDeleteConfirm(null)}>x</button>
            </div>
            <div className="mkt-set-modal-body">
              <div className="mkt-set-confirm-warning">
                <AlertTriangle size={24} />
                <p>Are you sure you want to delete this status? This action cannot be undone. Deletion will fail if any leads are currently using this status.</p>
              </div>
            </div>
            <div className="mkt-set-modal-footer">
              <button className="mkt-set-btn-secondary" onClick={() => setLsDeleteConfirm(null)}>
                Cancel
              </button>
              <button className="mkt-set-btn-danger" onClick={() => lsDeleteStatus(lsDeleteConfirm)}>
                <Trash2 size={16} />
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const zohoForm = crmForm.config?.zoho || {};
  const isZohoProvider = crmForm.provider === 'ZOHO';
  const isWebhookProvider = ['WEBHOOK', 'CUSTOM'].includes(crmForm.provider);
  const canSendCrmTest = isZohoProvider
    ? Boolean(zohoForm.clientId && (zohoForm.clientSecret || crmIntegration?.config?.zoho?.hasClientSecret) && (zohoForm.refreshToken || crmIntegration?.config?.zoho?.hasRefreshToken))
    : Boolean(crmForm.webhookUrl);

  const crmSyncTabContent = (
    <div className="mkt-set-crm-container">
      <div className="mkt-set-crm-header">
        <div>
          <h1>External CRM Sync</h1>
          <p>Send normalized Revstar contacts to a CRM, automation tool, or custom webhook.</p>
        </div>
        <div className={`mkt-set-crm-status ${crmIntegration?.isEnabled ? 'active' : 'inactive'}`}>
          {crmIntegration?.isEnabled ? <CheckCircle size={16} /> : <AlertTriangle size={16} />}
          {crmIntegration?.isEnabled ? 'Enabled' : 'Disabled'}
        </div>
      </div>

      {crmLoading ? (
        <div className="mkt-set-loading">Loading CRM settings...</div>
      ) : (
        <div className="mkt-set-crm-grid">
          <div className="mkt-set-crm-card">
            <div className="mkt-set-crm-card-head">
              <Globe size={20} />
              <div>
                <h2>{isZohoProvider ? 'Zoho CRM Connector' : 'Webhook Connector'}</h2>
                <p>
                  {isZohoProvider
                    ? 'Sync Revstar contacts into Zoho CRM Contacts with secure OAuth refresh tokens.'
                    : 'Use this for Zoho Flow, Zapier, Make, HubSpot workflows, or a custom CRM bridge.'}
                </p>
              </div>
            </div>

            <div className="mkt-set-crm-form">
              <label className="mkt-set-crm-field">
                <span>Provider</span>
                <select
                  value={crmForm.provider}
                  onChange={(e) => setCrmForm(prev => ({ ...prev, provider: e.target.value }))}
                >
                  <option value="WEBHOOK">Webhook</option>
                  <option value="ZOHO">Zoho CRM</option>
                  <option value="CUSTOM">Custom Webhook</option>
                  <option value="HUBSPOT">HubSpot - coming soon</option>
                  <option value="SALESFORCE">Salesforce - coming soon</option>
                </select>
              </label>

              <label className="mkt-set-crm-field">
                <span>Integration name</span>
                <input
                  type="text"
                  value={crmForm.name}
                  onChange={(e) => setCrmForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Zoho Flow, HubSpot webhook..."
                />
              </label>

              {isWebhookProvider && (
                <>
                  <label className="mkt-set-crm-field mkt-set-crm-field-wide">
                    <span>Webhook URL</span>
                    <input
                      type="url"
                      value={crmForm.webhookUrl}
                      onChange={(e) => setCrmForm(prev => ({ ...prev, webhookUrl: e.target.value }))}
                      placeholder="https://hooks.example.com/revstar-contact-sync"
                    />
                  </label>

                  <label className="mkt-set-crm-field">
                    <span>Signing secret</span>
                    <input
                      type="password"
                      value={crmForm.secret}
                      onChange={(e) => setCrmForm(prev => ({ ...prev, secret: e.target.value, clearSecret: false }))}
                      placeholder={crmIntegration?.hasSecret ? `Configured (${crmIntegration.secretPreview})` : 'Optional HMAC secret'}
                    />
                  </label>
                </>
              )}

              {isZohoProvider && (
                <div className="mkt-set-crm-zoho-panel">
                  <label className="mkt-set-crm-field">
                    <span>Zoho data center</span>
                    <select
                      value={zohoForm.dataCenter || 'US'}
                      onChange={(e) => {
                        const selected = zohoDataCenters[e.target.value] || zohoDataCenters.US;
                        updateZohoForm({
                          dataCenter: e.target.value,
                          accountsUrl: selected.accountsUrl,
                          apiDomain: selected.apiDomain
                        });
                      }}
                    >
                      {Object.entries(zohoDataCenters).map(([key, value]) => (
                        <option key={key} value={key}>{value.label}</option>
                      ))}
                    </select>
                  </label>

                  <label className="mkt-set-crm-field">
                    <span>Module</span>
                    <select
                      value={zohoForm.module || 'Contacts'}
                      onChange={(e) => updateZohoForm({ module: e.target.value })}
                    >
                      <option value="Contacts">Contacts</option>
                      <option value="Leads">Leads</option>
                    </select>
                  </label>

                  <label className="mkt-set-crm-field mkt-set-crm-field-wide">
                    <span>Client ID</span>
                    <input
                      type="text"
                      value={zohoForm.clientId || ''}
                      onChange={(e) => updateZohoForm({ clientId: e.target.value })}
                      placeholder="Zoho OAuth client ID"
                    />
                  </label>

                  <label className="mkt-set-crm-field">
                    <span>Client secret</span>
                    <input
                      type="password"
                      value={zohoForm.clientSecret || ''}
                      onChange={(e) => updateZohoForm({ clientSecret: e.target.value, clearClientSecret: false })}
                      placeholder={crmIntegration?.config?.zoho?.hasClientSecret ? 'Configured' : 'Zoho OAuth client secret'}
                    />
                  </label>

                  <label className="mkt-set-crm-field">
                    <span>Refresh token</span>
                    <input
                      type="password"
                      value={zohoForm.refreshToken || ''}
                      onChange={(e) => updateZohoForm({ refreshToken: e.target.value, clearRefreshToken: false })}
                      placeholder={crmIntegration?.config?.zoho?.hasRefreshToken ? 'Configured' : 'Zoho OAuth refresh token'}
                    />
                  </label>

                  <label className="mkt-set-crm-field">
                    <span>Accounts URL</span>
                    <input
                      type="url"
                      value={zohoForm.accountsUrl || ''}
                      onChange={(e) => updateZohoForm({ accountsUrl: e.target.value })}
                    />
                  </label>

                  <label className="mkt-set-crm-field">
                    <span>API domain</span>
                    <input
                      type="url"
                      value={zohoForm.apiDomain || ''}
                      onChange={(e) => updateZohoForm({ apiDomain: e.target.value })}
                    />
                  </label>

                  {(crmIntegration?.config?.zoho?.hasClientSecret || crmIntegration?.config?.zoho?.hasRefreshToken) && (
                    <label className="mkt-set-crm-toggle-row subtle">
                      <input
                        type="checkbox"
                        checked={Boolean(zohoForm.clearClientSecret || zohoForm.clearRefreshToken)}
                        onChange={(e) => updateZohoForm({
                          clearClientSecret: e.target.checked,
                          clearRefreshToken: e.target.checked,
                          clientSecret: '',
                          refreshToken: ''
                        })}
                      />
                      <span>Remove saved Zoho secret and refresh token</span>
                    </label>
                  )}
                </div>
              )}

              <label className="mkt-set-crm-toggle-row">
                <input
                  type="checkbox"
                  checked={crmForm.isEnabled}
                  onChange={(e) => setCrmForm(prev => ({ ...prev, isEnabled: e.target.checked }))}
                />
                <span>Enable automatic CRM sync after the connection is tested.</span>
              </label>

              {isWebhookProvider && crmIntegration?.hasSecret && (
                <label className="mkt-set-crm-toggle-row subtle">
                  <input
                    type="checkbox"
                    checked={crmForm.clearSecret}
                    onChange={(e) => setCrmForm(prev => ({ ...prev, clearSecret: e.target.checked, secret: '' }))}
                  />
                  <span>Remove saved signing secret</span>
                </label>
              )}
            </div>

            <div className="mkt-set-crm-actions">
              <button className="mkt-set-btn-secondary" onClick={sendCrmTest} disabled={crmTesting || !canSendCrmTest}>
                <Zap size={16} />
                {crmTesting ? 'Sending...' : 'Send test'}
              </button>
              <button className="mkt-set-btn-primary" onClick={saveCrmSettings} disabled={crmSaving}>
                <Check size={16} />
                {crmSaving ? 'Saving...' : 'Save integration'}
              </button>
            </div>
          </div>

          <div className="mkt-set-crm-card mkt-set-crm-bulk-card">
            <div className="mkt-set-crm-card-head">
              <Users size={20} />
              <div>
                <h2>Bulk Contact Sync</h2>
                <p>Backfill recent contacts in small, controlled batches. Preview before sending to Zoho or your webhook.</p>
              </div>
            </div>

            <div className="mkt-set-crm-bulk-controls">
              <label className="mkt-set-crm-field">
                <span>Contact type</span>
                <select
                  value={crmBulkForm.contactType}
                  onChange={(e) => setCrmBulkForm(prev => ({ ...prev, contactType: e.target.value, offset: 0 }))}
                >
                  <option value="MERCHANT">Merchants</option>
                  <option value="LEAD">Website leads</option>
                  <option value="WAITLIST">Waitlist</option>
                  <option value="CUSTOMER">Customers</option>
                </select>
              </label>

              <label className="mkt-set-crm-field">
                <span>Batch size</span>
                <input
                  type="number"
                  min="1"
                  max="100"
                  value={crmBulkForm.limit}
                  onChange={(e) => setCrmBulkForm(prev => ({ ...prev, limit: e.target.value }))}
                />
              </label>

              <label className="mkt-set-crm-field">
                <span>Offset</span>
                <input
                  type="number"
                  min="0"
                  value={crmBulkForm.offset}
                  onChange={(e) => setCrmBulkForm(prev => ({ ...prev, offset: e.target.value }))}
                />
              </label>
            </div>

            {crmBulkResult && (
              <div className={`mkt-set-crm-bulk-summary ${(crmBulkResult.summary?.failed || 0) > 0 ? 'has-failures' : ''}`}>
                <strong>
                  {crmBulkResult.dryRun ? 'Preview' : 'Last bulk sync'}:
                  {' '}
                  {crmBulkResult.processed || crmBulkResult.limit || 0}
                  {' '}
                  of {crmBulkResult.total || 0}
                </strong>
                <span>
                  Next offset: {crmBulkResult.nextOffset ?? 0}
                  {crmBulkResult.summary
                    ? ` · Success ${crmBulkResult.summary.success || 0}, Failed ${crmBulkResult.summary.failed || 0}, Skipped ${crmBulkResult.summary.skipped || 0}`
                    : ''}
                </span>
                {crmBulkResult.dryRun && (
                  <span>This is a dry run. No contacts were sent.</span>
                )}
                {!crmBulkResult.dryRun && crmBulkResult.throttleMs > 0 && (
                  <span>Zoho throttling: {crmBulkResult.throttleMs}ms between records.</span>
                )}
                {(crmBulkResult.failures || []).length > 0 && (
                  <div className="mkt-set-crm-bulk-failures">
                    {crmBulkResult.failures.map((failure, index) => (
                      <span key={`${failure.contactId || 'failure'}-${index}`}>
                        {failure.contactType || 'Contact'} {failure.contactId ? failure.contactId.slice(0, 8) : ''}
                        {' '}
                        - {getCrmFailureMessage(failure)}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="mkt-set-crm-actions">
              <button
                className="mkt-set-btn-secondary"
                onClick={() => runCrmBulkSync({ dryRun: true })}
                disabled={crmBulkPreviewing || crmBulkSyncing}
              >
                <Eye size={16} />
                {crmBulkPreviewing ? 'Previewing...' : 'Preview batch'}
              </button>
              <button
                className="mkt-set-btn-primary"
                onClick={() => runCrmBulkSync({ dryRun: false })}
                disabled={crmBulkSyncing || crmBulkPreviewing || !crmIntegration}
              >
                <Zap size={16} />
                {crmBulkSyncing ? 'Syncing...' : 'Sync batch'}
              </button>
            </div>
          </div>

          <div className="mkt-set-crm-card">
            <div className="mkt-set-crm-card-head">
              <History size={20} />
              <div>
                <h2>Recent Deliveries</h2>
                <p>Delivery logs make CRM sync auditable and easier to debug.</p>
              </div>
            </div>

            <div className="mkt-set-crm-log-list">
              {crmLogs.length === 0 ? (
                <div className="mkt-set-crm-empty">No CRM sync attempts yet.</div>
              ) : (
                crmLogs.map(log => (
                  <div key={log.id} className={`mkt-set-crm-log mkt-set-crm-log-${String(log.status || '').toLowerCase()}`}>
                    <div>
                      <strong>{String(log.event || '').replace(/_/g, ' ')}</strong>
                      <span>{log.contactType || 'TEST'} {log.contactId ? `· ${log.contactId.slice(0, 8)}` : ''}</span>
                    </div>
                    <div className="mkt-set-crm-log-meta">
                      <span>{log.status}</span>
                      <span>{log.responseStatus || log.errorMessage || 'No response'}</span>
                      <span>{log.createdAt ? new Date(log.createdAt).toLocaleString() : ''}</span>
                    </div>
                    {(log.errorMessage || log.responseBody) && (
                      <div className="mkt-set-crm-log-error">{getCrmFailureMessage(log)}</div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // Bulk Update Modal
  const BulkUpdateModal = () => {
    const [bulkSettings, setBulkSettings] = useState({
      emailMarketing: false,
      smsMarketing: false,
      productUpdates: false,
      platformAnnouncements: false,
      newsletterSubscription: false,
      promotionalCampaigns: false,
      educationalContent: false
    });
    const [bulkGlobalOptOut, setBulkGlobalOptOut] = useState(false);

    return (
      <div className="mkt-set-modal-overlay" onClick={() => setShowBulkUpdate(false)}>
        <div className="mkt-set-modal" onClick={(e) => e.stopPropagation()}>
          <div className="mkt-set-modal-header">
            <h2>Bulk Update Preferences</h2>
            <button className="mkt-set-btn-icon" onClick={() => setShowBulkUpdate(false)}>x</button>
          </div>
          <div className="mkt-set-modal-body">
            <p>Update preferences for {bulkSelection.length} selected merchants</p>

            <div className="mkt-set-bulk-section-label">Communication Channels</div>
            {Object.entries(bulkSettings).map(([key, value]) => (
              <label key={key} className="mkt-set-bulk-checkbox">
                <input
                  type="checkbox"
                  checked={value}
                  onChange={(e) => setBulkSettings({...bulkSettings, [key]: e.target.checked})}
                  disabled={bulkGlobalOptOut}
                />
                <span>{key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}</span>
              </label>
            ))}

            <div className="mkt-set-bulk-divider" />
            <div className="mkt-set-bulk-section-label mkt-set-bulk-danger-label">Global Opt-out</div>
            <label className="mkt-set-bulk-checkbox mkt-set-bulk-danger">
              <input
                type="checkbox"
                checked={bulkGlobalOptOut}
                onChange={(e) => {
                  setBulkGlobalOptOut(e.target.checked);
                  if (e.target.checked) {
                    setBulkSettings({
                      emailMarketing: false,
                      smsMarketing: false,
                      productUpdates: false,
                      platformAnnouncements: false,
                      newsletterSubscription: false,
                      promotionalCampaigns: false,
                      educationalContent: false
                    });
                  }
                }}
              />
              <span>Opt out from ALL marketing communications</span>
            </label>
            {bulkGlobalOptOut && (
              <p className="mkt-set-bulk-warning">This will disable all channels and set global opt-out for selected merchants.</p>
            )}
          </div>
          <div className="mkt-set-modal-footer">
            <button className="mkt-set-btn-secondary" onClick={() => setShowBulkUpdate(false)}>
              Cancel
            </button>
            <button className="mkt-set-btn-primary" onClick={() => bulkUpdate({ ...bulkSettings, ...(bulkGlobalOptOut ? { globalOptOut: true } : {}) })}>
              Update All
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="mkt-set-dashboard">
      {/* Status Message Banner */}
      <StatusBanner />

      {/* Tabs */}
      <div className="mkt-set-tabs">
        <button
          className={`mkt-set-tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          <TrendingUp size={18} />
          Overview
        </button>
        <button
          className={`mkt-set-tab ${activeTab === 'merchants' ? 'active' : ''}`}
          onClick={() => setActiveTab('merchants')}
        >
          <Users size={18} />
          Manage Merchants
        </button>
        <button
          className={`mkt-set-tab ${activeTab === 'lead-statuses' ? 'active' : ''}`}
          onClick={() => setActiveTab('lead-statuses')}
        >
          <Tag size={18} />
          Lead Statuses
        </button>
        <button
          className={`mkt-set-tab ${activeTab === 'crm-sync' ? 'active' : ''}`}
          onClick={() => setActiveTab('crm-sync')}
        >
          <Globe size={18} />
          CRM Sync
        </button>
      </div>

      {/* Content */}
      {activeTab === 'overview' && <OverviewDashboard />}
      {activeTab === 'merchants' && <MerchantsListView />}
      {activeTab === 'detail' && <MerchantDetailView />}
      {activeTab === 'lead-statuses' && leadStatusesTabContent}
      {activeTab === 'crm-sync' && crmSyncTabContent}

      {/* Global Opt-out Confirmation Modal */}
      {showOptOutConfirm && (
        <div className="mkt-set-modal-overlay" onClick={() => setShowOptOutConfirm(false)}>
          <div className="mkt-set-modal" onClick={(e) => e.stopPropagation()}>
            <div className="mkt-set-modal-header">
              <h2>Confirm Global Opt-out Change</h2>
              <button className="mkt-set-btn-icon" onClick={() => setShowOptOutConfirm(false)}>x</button>
            </div>
            <div className="mkt-set-modal-body">
              {pendingOptOutValue ? (
                <>
                  <div className="mkt-set-confirm-warning">
                    <AlertTriangle size={24} />
                    <p>This will <strong>opt out</strong> the merchant from ALL marketing communications. All individual channel preferences will be disabled.</p>
                  </div>
                </>
              ) : (
                <p>This will restore the merchant's ability to receive marketing communications. Individual channel preferences can then be managed separately.</p>
              )}
            </div>
            <div className="mkt-set-modal-footer">
              <button className="mkt-set-btn-secondary" onClick={() => setShowOptOutConfirm(false)}>
                Cancel
              </button>
              <button
                className={pendingOptOutValue ? 'mkt-set-btn-danger' : 'mkt-set-btn-primary'}
                onClick={() => {
                  updatePreference(merchantDetail.merchantId, { globalOptOut: pendingOptOutValue });
                  setShowOptOutConfirm(false);
                }}
              >
                {pendingOptOutValue ? 'Confirm Opt-out' : 'Restore Communications'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Update Modal */}
      {showBulkUpdate && <BulkUpdateModal />}
    </div>
  );
}

export default MarketingSettings;
